import { isNil, union, uniq } from 'lodash';
import { Province, ProvinceAbbreviation } from '~domain/model/core/address/province';
import { Carrier } from '~domain/model/core/carrier';
import { ProductType } from '~domain/model/core/product-type';
import { CarrierProductsByProvinceConfig, CarrierProductsConfig, CarriersConfig } from './carriers-config.type';

class CarriersConfigFacade {
  private configs: CarriersConfig;
  // Define which carrier insurance products can be quoted from product type and province
  private quotableCarrierProducts: Map<ProductType, Map<Province, string[]>>;
  // Define which carrier insurance products can be sell from product type and province
  private saleableCarrierProducts: Map<ProductType, Map<Province, string[]>>;

  constructor() {
    this.configs = require('../../../configs/carriers-config.json');

    const { quotable, saleable } = this.configs.carriers.products;

    // Initialize quotable and saleable insurance products
    this.quotableCarrierProducts = this.createProductProvinceDefinition(quotable);
    this.saleableCarrierProducts = this.createProductProvinceDefinition(saleable);
  }

  /**
   * Returns the list of insurance products offered by carrier
   * @param name carrier's name
   */
  public getCarrierCodesFromName(name: Carrier): string[] {
    const carrierDefinition: CarrierProductsConfig = this.configs.carriers.products.all.find(
      (def: CarrierProductsConfig) => def.name === name,
    );
    if (!isNil(carrierDefinition)) return carrierDefinition.codes;
  }

  /**
   * Returns the name of the carrier that offers the insurance product code
   * @param code
   */
  public getCarrierFromCode(code: string): Carrier {
    const carrierDefinition: CarrierProductsConfig = this.configs.carriers.products.all.find(
      (def: CarrierProductsConfig) => def.codes.includes(code),
    );
    if (!isNil(carrierDefinition)) return carrierDefinition.name;
  }

  /**
   * Returns all quotable insurance product codes for a specific product and province
   * @param product mandatory
   * @param province mandatory
   * @param carrier optional return codes the carrier offers
   */
  public getQuotableCarrierCodes(product: ProductType, province: Province, carrier?: Carrier): string[] {
    const quotableProducts: string[] = this.quotableCarrierProducts.get(product).get(province);

    if (isNil(carrier)) return quotableProducts;

    return quotableProducts.filter((code) => this.getCarrierCodesFromName(carrier).includes(code));
  }

  /**
   * Returns all quotable carrier for a specific product and province
   * @param productTypes list of product types
   * @param provinces list of provinces
   */
  public getQuotableCarriers(productTypes: ProductType[], provinces: Province[]): Carrier[] {
    const carriers = new Set<Carrier>();

    productTypes.forEach((productType) => {
      provinces.forEach((province) => {
        this.quotableCarrierProducts
          .get(productType)
          .get(province)
          .map((carrierCode) => this.getCarrierFromCode(carrierCode))
          .forEach((carrier) => carriers.add(carrier));
      });
    });

    return Array.from(carriers.values());
  }

  /**
   * Returns all saleable insurance product codes for a specific product and province
   * @param product optional. If not defined, it returns all insurance product codes (all product + all province)
   * @param province optional. If not defined, it returns all insurance product codes for the specific product, but for all provinces
   */
  public getSalableCarrierCodes(product?: ProductType, province?: Province, carrier?: Carrier): string[] {
    if (isNil(product)) {
      return this.mergeProducts(this.saleableCarrierProducts).filter(
        (carrierCode) => isNil(carrier) || this.isCodeFromCarrier(carrier, carrierCode),
      );
    }

    if (isNil(province)) {
      return this.mergeProvinces(this.saleableCarrierProducts.get(product)).filter(
        (carrierCode) => isNil(carrier) || this.isCodeFromCarrier(carrier, carrierCode),
      );
    }

    const saleableCarrierCodes = this.saleableCarrierProducts.get(product).get(province);
    if (isNil(carrier)) {
      return saleableCarrierCodes;
    }

    const carrierCodes = this.getCarrierCodesFromName(carrier);
    return saleableCarrierCodes.filter((carrierCode) => carrierCodes.includes(carrierCode));
  }

  /**
   * Returns all saleable carriers for a specific product and province
   * @param product optional. If not defined, it returns all carriers (all products + all provinces)
   * @param province optional. If not defined, it returns all insurance carriers for the specified product, but for all provinces
   */
  public getSalableCarriers(product?: ProductType, province?: Province): Carrier[] {
    if (isNil(product)) return this.codesToCarriers(this.mergeProducts(this.saleableCarrierProducts));
    if (isNil(province)) return this.codesToCarriers(this.mergeProvinces(this.saleableCarrierProducts.get(product)));

    return this.codesToCarriers(this.saleableCarrierProducts.get(product).get(province));
  }

  // Returns the list of sub standard carriers
  public getSubStandardCarriers(): Carrier[] {
    return this.configs.carriers.subStandards;
  }

  /**
   * Returns true if the carrier offers the insurance product code
   * @param carrier mandatory
   * @param code mandatory
   */
  public isCodeFromCarrier(carrier: Carrier, code: string): boolean {
    return this.configs.carriers.products.all.some(
      (def: CarrierProductsConfig) => def.name === carrier && def.codes.includes(code),
    );
  }

  public isSaleableCarrier(carrier: Carrier): boolean {
    const saleableCarrierCodes = this.getSalableCarrierCodes();
    return this.getCarrierCodesFromName(carrier).some((carrierCode) => saleableCarrierCodes.includes(carrierCode));
  }

  /** Determines if given Carrier supports yearly billing. Defaults to true */
  public isYearlyBillingSupported(carrier: Carrier): boolean {
    const carrierConfig = this.configs?.carriers?.products?.all?.find((config) => config.name === carrier);
    // By default, consider yearly billing enabled if it's not set in the config
    if (isNil(carrierConfig?.hasYearlyBillingSupport)) {
      return true;
    }
    return carrierConfig.hasYearlyBillingSupport;
  }

  // Create from JSON object mapping the property object
  private createProductProvinceDefinition(
    config: CarrierProductsByProvinceConfig,
  ): Map<ProductType, Map<Province, string[]>> {
    const productProvinceDefinition: Map<ProductType, Map<Province, string[]>> = new Map();

    Object.keys(config).forEach((product: ProductType) => {
      // For each product, create a province mapper
      const temp: Map<Province, string[]> = new Map();
      Object.keys(config[product]).forEach((provinceAbr: string) => {
        temp.set(ProvinceAbbreviation.get(provinceAbr), config[product][provinceAbr]);
      });

      productProvinceDefinition.set(product, temp);
    });

    return productProvinceDefinition;
  }

  // Merge all insurance product codes, all products and provinces mixed up
  private mergeProducts(productProvinceDefinition: Map<ProductType, Map<Province, string[]>>): string[] {
    return Array.from(productProvinceDefinition.values()).reduce(
      (result: string[], current: Map<Province, string[]>) => {
        return union(result, this.mergeProvinces(current));
      },
      [],
    );
  }

  // Merge all insurance product codes, all province mixed up
  private mergeProvinces(provinceDefinition: Map<Province, string[]>): string[] {
    return Array.from(provinceDefinition.values()).reduce((result: string[], current: string[]) => {
      return union(result, current);
    }, []);
  }

  private codesToCarriers(codes: string[]) {
    if (isNil(codes)) return [];

    return uniq(codes.map((x) => this.getCarrierFromCode(x)));
  }
}

export const carriersConfig = new CarriersConfigFacade();
